import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import KeyTakeaways from '../components/KeyTakeaways';
import SummaryVideo from '../components/SummaryVideo';
import FullVideo from '../components/FullVideo';
import DetailedNotes from '../components/DetailedNotes';
import Activity from '../components/Activity';
import Sidebar from '../components/Sidebar';
import LectureHighlights from '../components/LectureHighlights';
import { fetchLectureById, fetchLectureData } from '../services/googleSheets';
import useMediaQuery from '../hooks/useMediaQuery';
import { useConfig } from '../config/ConfigContext';
import FloatingActionButton from '../components/home/FloatingActionButton';
import AIContentDisclaimer from '../components/AIContentDisclaimer';
import { metaTagsService } from '../services/metaTagsService';

/**
 * Inline share button for video sections in the Watch tab
 */
const VideoShareButton = ({ label, url, theme, lectureHeaderRef, lecture, config }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareTitle = lecture?.title || label;
    const centerName = config?.name || '';
    const shareText = `${shareTitle}${lecture?.speaker ? ` — ${lecture.speaker}` : ''}${centerName ? ` | ${centerName}` : ''}`;

    // Try to capture the lecture header as a share image
    let shareFile = null;
    if (lectureHeaderRef?.current && navigator.canShare) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(lectureHeaderRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: theme.colors.primary,
        });
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (blob) {
          shareFile = new File([blob], `${(lecture?.title || 'lecture').replace(/[^a-z0-9]/gi, '-')}.png`, { type: 'image/png' });
        }
      } catch { /* screenshot failed — share without image */ }
    }

    if (navigator.share) {
      try {
        const shareData = { title: shareTitle, text: shareText, url };
        if (shareFile && navigator.canShare({ files: [shareFile] })) {
          shareData.files = [shareFile];
        }
        await navigator.share(shareData);
      } catch (e) { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(`${shareText}\n${url}`); } catch (e) { /* fallback */ }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 20px', marginTop: '-8px', marginBottom: '16px' }}>
      <button
        onClick={handleShare}
        aria-label={`Share ${label}`}
        style={{
          background: 'none',
          border: `1px solid ${theme.colors.mediumGray || '#ddd'}`,
          borderRadius: '20px',
          padding: '5px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '0.8em',
          color: copied ? theme.colors.primary : '#666',
          fontWeight: copied ? 'bold' : 'normal',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
        {copied ? 'Link Copied!' : 'Share'}
      </button>
    </div>
  );
};

/**
 * LecturePage component to display lecture content with tabs
 * @returns {JSX.Element} LecturePage component
 */
const LecturePage = () => {
  const { lectureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lectureHeaderRef = useRef(null);
  const fromVideoId = location.state?.fromVideoId;
  const { config, theme, loading: configLoading, error: configError } = useConfig(); // Get theme too
  const deepLinkHandled = useRef(false);

  const [lecture, setLecture] = useState(null);
  const [allLectures, setAllLectures] = useState([]);
  const [seriesLectures, setSeriesLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // Initialize activeTab

  // Check if the current lecture has highlights content (infographics, viral clips, or dubbed video)
  const hasHighlightsContent = lecture?.infographicsUrls?.trim() || lecture?.viralClipsUrls?.trim() || lecture?.dubbedVideoUrl?.trim();

  // Define available tabs based on config flags and lecture data
  const availableTabs = [
    config?.key_takeaways && { id: 'key', label: 'Key Takeaways' },
    hasHighlightsContent && { id: 'highlights', label: 'Highlights' },
    (config?.full_video || config?.summary_video) && { id: 'watch', label: 'Watch' },
    config?.detailed_notes && { id: 'notes', label: 'Detailed Notes' },
    config?.activity && { id: 'activity', label: 'Activity' },
  ].filter(Boolean); // Filter out falsy values (disabled tabs)

  // Fetch lecture data when component mounts or config changes
  useEffect(() => {
    const loadLecture = async () => {
      // Wait for config and lectureId
      if (configLoading || !config || !lectureId) {
        console.log('LecturePage: Waiting for config/lectureId...');
        return;
      }
      // Handle config error
      if (configError) {
        console.error('LecturePage: Config loading failed:', configError);
        setError(`Failed to load configuration: ${configError.message || configError}`);
        setLoading(false);
        return;
      }
      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('LecturePage: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in the configuration.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`LecturePage: Fetching data for sheet ID: ${config.google_sheet_id}, Lecture ID: ${lectureId}`);
        // Fetch all lectures to enable navigation between lectures using the dynamic sheet ID
        const allLectureData = await fetchLectureData(config.google_sheet_id); // Pass sheetId
        setAllLectures(allLectureData);

        // Fetch specific lecture data using the dynamic sheet ID
        const lectureData = await fetchLectureById(config.google_sheet_id, lectureId); // Pass sheetId and lectureId

        if (!lectureData) {
          setError(`Lecture with ID '${lectureId}' not found.`); // More specific error
          setLoading(false);
          return;
        }

        // Filter lectures in the same series (trim to handle whitespace variations from Sheets)
        const currentSeries = (lectureData.event || '').trim();
        const lecturesInSeries = allLectureData.filter(l => (l.event || '').trim() === currentSeries);

        // Sort lectures in series by their lectureIdInSeries, or by date for recurring events
        const sortedSeriesLectures = [...lecturesInSeries].sort((a, b) => {
          const aHasLectureNum = a.lectureIdInSeries && a.lectureIdInSeries.toString().trim() !== '';
          const bHasLectureNum = b.lectureIdInSeries && b.lectureIdInSeries.toString().trim() !== '';

          // If both have lecture numbers, sort by lecture number
          if (aHasLectureNum && bHasLectureNum) {
            const aId = parseInt(a.lectureIdInSeries.replace(/\D/g, '')) || 0;
            const bId = parseInt(b.lectureIdInSeries.replace(/\D/g, '')) || 0;
            return aId - bId;
          }

          // If neither has lecture numbers (recurring events), sort by date
          if (!aHasLectureNum && !bHasLectureNum) {
            const aDate = a.date ? new Date(a.date).getTime() : 0;
            const bDate = b.date ? new Date(b.date).getTime() : 0;
            return aDate - bDate;
          }

          // Mixed case: put numbered lectures before unnumbered ones
          if (aHasLectureNum && !bHasLectureNum) return -1;
          if (!aHasLectureNum && bHasLectureNum) return 1;

          // Fall back to global lectureId
          const aId = parseInt(a.lectureId.replace(/\D/g, '')) || 0;
          const bId = parseInt(b.lectureId.replace(/\D/g, '')) || 0;
          return aId - bId;
        });

        setSeriesLectures(sortedSeriesLectures);

        // Enhance lecture data with fallback values for URLs
        const enhancedLectureData = {
          ...lectureData,
          // If these fields don't exist in the data, add placeholder URLs
          keyTakeawaysUrl: lectureData.keyTakeawaysUrl || '',
          detailedNotesUrl: lectureData.detailedNotesUrl || '',
          // Add placeholder URLs for videos if they don't exist
          summaryVideoUrl: lectureData.summaryVideoUrl || '',
          fullVideoUrl: lectureData.fullVideoUrl || ''
        };

        setLecture(enhancedLectureData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading lecture:', error);
        setError('An error occurred while loading the lecture. Please try again later.');
        setLoading(false);
      }
    };

    loadLecture();
  }, [lectureId, config, configLoading, configError, activeTab]); // Add activeTab to ensure it gets set

  // Scroll to top when lecture changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lectureId]);

  // Update meta tags for social sharing when lecture data loads
  useEffect(() => {
    if (!lecture) return;
    const centerName = config?.name || '';
    const title = `${lecture.title || 'Lecture'}${lecture.speaker ? ` — ${lecture.speaker}` : ''}`;
    const description = [
      lecture.event && `Series: ${lecture.event}`,
      lecture.speaker && `Speaker: ${lecture.speaker}`,
      lecture.date && `Date: ${lecture.date}`,
      centerName && `Center: ${centerName}`,
    ].filter(Boolean).join(' | ');

    // Extract YouTube thumbnail from summary or full video URL
    const ytMatch = (lecture.summaryVideoUrl || lecture.fullVideoUrl || '').match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    );
    const thumbnail = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;

    metaTagsService.updateMetaTags({
      title: `${title}${centerName ? ` | ${centerName}` : ''}`,
      description,
      image: thumbnail || config?.logo_url || '',
      url: window.location.href,
      type: 'article',
    });

    // Update page title
    document.title = `${title}${centerName ? ` | ${centerName}` : ''}`;

    return () => {
      metaTagsService.resetToDefaults();
    };
  }, [lecture, config]);

  // Set initial active tab once config is loaded and available tabs are determined
  if (!configLoading && !configError && availableTabs.length > 0 && activeTab === null) {
    setActiveTab(availableTabs[0].id);
  }

  // Handle deep link: switch to URL-specified tab after lecture data loads
  useEffect(() => {
    if (deepLinkHandled.current || !lecture) return;
    const tabParam = searchParams.get('tab');
    if (tabParam && availableTabs.find(t => t.id === tabParam)) {
      setActiveTab(tabParam);
      deepLinkHandled.current = true;
    }
  }, [lecture, availableTabs, searchParams]);

  // Parse clip index from URL for deep linking into a specific viral clip
  const clipParam = searchParams.get('clip');
  const initialClipIndex = clipParam !== null && !isNaN(parseInt(clipParam, 10))
    ? parseInt(clipParam, 10)
    : null;

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle navigation to previous or next lecture within the same series
  const handleNavigation = (direction) => {
    if (!seriesLectures.length) return;

    // Find current lecture index in the series
    const currentIndex = seriesLectures.findIndex(l => l.lectureId === lectureId);
    if (currentIndex === -1) return;

    let targetIndex;
    if (direction === 'prev') {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : seriesLectures.length - 1;
    } else {
      targetIndex = currentIndex < seriesLectures.length - 1 ? currentIndex + 1 : 0;
    }

    // Navigate to the target lecture
    navigate(`/lecture/${seriesLectures[targetIndex].lectureId}`);
  };

  // Determine which layout to use based on device type
  // Use the custom hook for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)');

  // For mobile, we need to wrap the MobileLayout to pass the current lecture data
  const Layout = ({ children }) => {
    if (isMobile) {
      return (
        <MobileLayout currentSectionTitle={lecture?.title || 'Lecture'}>
          {children}
        </MobileLayout>
      );
    }

    return <DesktopLayout>{children}</DesktopLayout>;
  };

  // Handle config loading/error state before rendering
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }

  // Show loading state
  if (loading) {
    return (
      <Layout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: theme.colors.primary }}>Loading...</h2>
            <p>Please wait while we fetch the lecture content.</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show error state
  if (error) {
    return (
      <Layout>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100vh',
          padding: '0 20px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'red' }}>Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/')} 
            style={{ 
              padding: '10px 20px', 
              backgroundColor: theme.colors.primary, 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              marginTop: '20px' 
            }}
          >
            Return to Home
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Conditionally render Sidebar only on desktop */}
      {!isMobile && <Sidebar />}
      {/* We no longer need to render MobileLectureMenu here as it's integrated in MobileHamburgerMenu */}
      <div className="lecture-page" style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        marginLeft: isMobile ? '0' : '250px' // Add margin for desktop to accommodate sidebar
      }}>
        {/* Back to Video banner — shown when navigated from VideoWatchPage */}
        {fromVideoId && (
          <div style={{
            padding: '10px 20px',
            background: theme.colors.primary + '10',
            borderBottom: `1px solid ${theme.colors.primary}30`,
            display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer',
            fontSize: '14px', fontWeight: 500,
            color: theme.colors.primary,
          }}
            onClick={() => navigate(`/videos/${fromVideoId}${searchParams.toString() ? '?' + searchParams.toString() : ''}`)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Video Player
          </div>
        )}

        {/* Lecture header */}
        <div
          ref={lectureHeaderRef}
          className="lecture-header"
          data-share-capture
          data-share-capture-bg={theme.colors.primary}
          data-share-title={lecture?.title || ''}
          data-share-text={[lecture?.speaker, config?.name].filter(Boolean).join(' | ')}
          style={{
            backgroundColor: theme.colors.primary,
            color: 'white',
            padding: '20px 30px',
            borderBottom: `4px solid ${theme.colors.secondary}`
          }}
        >
          <h1 style={{ 
            margin: '0 0 10px 0',
            fontSize: '1.8rem',
            fontFamily: theme.fonts.heading
          }}>
            {lecture?.title || 'Lecture Title'}
          </h1>

          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '15px',
            fontSize: '0.9rem'
          }}>
            {lecture?.event && (
              <span
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                onClick={() => navigate(`/events/${encodeURIComponent(lecture.event)}${searchParams.toString() ? '?' + searchParams.toString() : ''}`)}
                title={`View all lectures in ${lecture.event}`}
              >
                <span style={{ marginRight: '5px' }}>📚</span>
                <strong>Series:</strong>&nbsp;{lecture.event}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '4px', opacity: 0.6 }}>
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </span>
            )}

            {lecture?.lectureIdInSeries && lecture.lectureIdInSeries.toString().trim() !== '' && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '5px' }}>🆔</span>
                <strong>Lecture {lecture.lectureIdInSeries}</strong>
              </span>
            )}

            {lecture?.speaker && (
              <span
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                onClick={() => navigate(`/speaker/${encodeURIComponent(lecture.speaker)}${searchParams.toString() ? '?' + searchParams.toString() : ''}`)}
                title={`View all lectures by ${lecture.speaker}`}
              >
                <span style={{ marginRight: '5px' }}>👤</span>
                {lecture.speaker}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '4px', opacity: 0.6 }}>
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </span>
            )}
            
            {lecture?.date && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '5px' }}>📅</span>
                {new Date(lecture.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>

        {/* Tabs navigation */}
        {availableTabs.length > 0 ? (
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.mediumGray}`,
            backgroundColor: 'white'
          }}>
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                style={{
                  padding: '15px 20px',
                  backgroundColor: activeTab === tab.id ? theme.colors.primary : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#333333',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? `3px solid ${theme.colors.secondary}` : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                  flex: 1,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  fontFamily: theme.fonts.menu
                }}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: theme.colors.text }}>
            No content sections are enabled for this lecture.
          </div>
        )}

        {/* Tab content */}
        <div className="tab-content" style={{
          flex: 1,
          padding: '20px',
          overflow: 'auto',
          backgroundColor: '#f9f9f9'
        }}>
          {activeTab === 'key' && config?.key_takeaways && <KeyTakeaways url={lecture?.keyTakeawaysUrl} />}
          {activeTab === 'watch' && (
            <>
              {config?.summary_video && lecture?.summaryVideoUrl && (
                <>
                  <SummaryVideo url={lecture.summaryVideoUrl} />
                  <VideoShareButton
                    label="Summary Video"
                    url={window.location.href}
                    theme={theme}
                    lectureHeaderRef={lectureHeaderRef}
                    lecture={lecture}
                    config={config}
                  />
                </>
              )}
              {config?.full_video && (
                <>
                  <FullVideo
                    url={lecture?.fullVideoUrl}
                    clipStartTime={lecture?.clipStartTime}
                    clipEndTime={lecture?.clipEndTime}
                  />
                  <VideoShareButton
                    label="Full Lecture"
                    url={window.location.href}
                    theme={theme}
                    lectureHeaderRef={lectureHeaderRef}
                    lecture={lecture}
                    config={config}
                  />
                </>
              )}
            </>
          )}
          {activeTab === 'notes' && config?.detailed_notes && (
            <DetailedNotes
              url={lecture?.detailedNotesUrl}
              speaker={lecture?.speaker}
              series={lecture?.event}
              date={lecture?.date}
            />
          )}
          {activeTab === 'highlights' && hasHighlightsContent && (
            <LectureHighlights
              infographicsUrls={lecture?.infographicsUrls}
              viralClipsUrls={lecture?.viralClipsUrls}
              dubbedVideoUrl={lecture?.dubbedVideoUrl}
              initialClipIndex={initialClipIndex}
              lectureTitle={lecture?.title}
            />
          )}
          {activeTab === 'activity' && config?.activity && <Activity />}
        </div>

        {/* AI Content Disclaimer - subtle footer placement */}
        <div style={{ padding: '0 20px', marginBottom: '4px' }}>
          <AIContentDisclaimer compact={isMobile} />
        </div>

        {/* Navigation buttons at the bottom of the page */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '15px 20px',
          backgroundColor: theme.colors.lightGray,
          borderTop: `1px solid ${theme.colors.mediumGray}`
        }}>
          <button 
            onClick={() => handleNavigation('prev')}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>←</span>
            Previous Lecture
          </button>

          <button 
            onClick={() => handleNavigation('next')}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            Next Lecture
            <span style={{ fontSize: '1.2rem' }}>→</span>
          </button>
        </div>
      </div>
      <FloatingActionButton
        centerName={config?.name}
        config={config}
        theme={theme}
        showSearch={true}
      />
    </Layout>
  );
};

export default LecturePage;
